import { KeyboardEvent, ReactNode } from 'react';

import {
  Box,
  Button,
  Flex,
  Input,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';

import type { ContactSearchType } from 'api/quote-marketplace';

export const CONTACT_SEARCH_TYPES: {
  value: ContactSearchType;
  label: string;
}[] = [
  { value: 'name', label: 'Surname' },
  { value: 'email', label: 'Email Address' },
  { value: 'postal_code', label: 'Postal Code' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'trading_name', label: 'Company Name' },
  { value: 'contact_person', label: 'Contact Person' },
];

interface ContactSearchBarProps {
  searchType: ContactSearchType;
  onSearchTypeChange: (searchType: ContactSearchType) => void;
  searchValue: string;
  onSearchValueChange: (searchValue: string) => void;
  onSearch: () => void;
  onReset: () => void;
  hasActiveSearch?: boolean;
  isDisabled?: boolean;
  actions?: ReactNode;
}

const ContactSearchBar = ({
  searchType,
  onSearchTypeChange,
  searchValue,
  onSearchValueChange,
  onSearch,
  onReset,
  hasActiveSearch = false,
  isDisabled = false,
  actions,
}: ContactSearchBarProps) => {
  const inputBg = useColorModeValue('white', 'navy.900');

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const showReset = searchValue.trim().length > 0 || hasActiveSearch;

  return (
    <Flex gap={3} align="flex-end" flexWrap="wrap">
      <Box minW="150px">
        <Select
          variant="main"
          value={searchType}
          onChange={(e) =>
            onSearchTypeChange(e.target.value as ContactSearchType)
          }
          onKeyDown={handleKeyDown}
          bg={inputBg}
          size="md"
          isDisabled={isDisabled}
        >
          {CONTACT_SEARCH_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </Select>
      </Box>
      <Box flex="1" minW="200px">
        <Input
          variant="main"
          placeholder={`Search by ${CONTACT_SEARCH_TYPES.find((t) => t.value === searchType)?.label.toLowerCase()}...`}
          value={searchValue}
          onChange={(e) => onSearchValueChange(e.target.value)}
          onKeyDown={handleKeyDown}
          bg={inputBg}
          size="md"
          isDisabled={isDisabled}
        />
      </Box>
      <Button
        leftIcon={<SearchIcon />}
        variant="brand"
        size="md"
        onClick={onSearch}
        isDisabled={isDisabled}
      >
        Search
      </Button>
      {showReset && (
        <Button
          leftIcon={<SmallCloseIcon />}
          variant="outline"
          size="md"
          onClick={onReset}
          isDisabled={isDisabled}
        >
          Reset
        </Button>
      )}
      {actions}
    </Flex>
  );
};

export default ContactSearchBar;
